import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, filter, takeUntil } from 'rxjs';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, EventMessage, EventType } from '@azure/msal-browser';
import { AuthService } from '../../services/auth.service';
import { PageHeaderComponent } from '../page-header/page-header.component';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, PageHeaderComponent],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  errorMessage: string = '';
  isLoading: boolean = false;
  
  // Token info display
  tokenInfo: { userName: string; expiresAt: string; isExpired: boolean } | null = null;
  isAuthenticated: boolean = false;

  private readonly _destroying$ = new Subject<void>();

  constructor(
    public authService: AuthService,
    public router: Router,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService
  ) {}

  ngOnInit(): void {
    // Initial auth check - don't redirect, just update state
    this.checkAuthStatus();
    // Listen for login success events
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        this.checkAuthStatus();
        this.isLoading = false;
        this.router.navigate(['/home']);
      });

    // Listen for login failure events
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        this.errorMessage = result.error?.message || 'Authentication failed. Please try again.';
        this.isLoading = false;
      });

    // Update auth status when interaction completes
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAuthStatus();
        this.isLoading = false;
        // Only redirect if user initiated login (isLoading was true)
      });
  }

  checkAuthStatus(): void {
    this.isAuthenticated = this.authService.isAuthenticated;
    this.tokenInfo = this.authService.getTokenInfo();
  }

  initiateLogin(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.authService.login();
  }

  logout(): void {
    this.authService.logout();
  }

  goToApp(): void {
    this.router.navigate(['/home']);
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }
}
